import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Button,
  ArrayField,
  Form,
  Typography,
  Toast,
  Upload,
  Modal,
  Tooltip,
} from "@douyinfe/semi-ui";
import { AxiosError } from "axios";
import type {
  BaseFormApi as FormApi,
  FormState,
} from "@douyinfe/semi-foundation/lib/es/form/interface";
import { uploadFileApi } from "@services/task/task.api";
import {
  downloadPdbFileApi,
  downloadAlphafoldApi,
} from "@services/task/task.api";
import { getBinderPlaceholder } from "@utils/data-process.util";
import { getGzipFormData } from "@utils/file.util";
import { debounce } from "lodash";
import { VHH_DESIGN_TYPES } from "@services/home/config/home.config";
import { VhhDesignTypeEnum } from "@services/home/enum/home.enum";
import { PluginUIContext } from "molstar/lib/mol-plugin-ui/context";
import classNames from "classnames";
import { getSitesLabelRange } from "molstar/lib/custom/utils/data.process.util";
import {
  getFoldSequenceStructureApi,
  checkCoordValidityApi,
} from "@services/home/home.api";
import { ReactComponent as MinusIcon } from "@assets/icons/minus_circle.svg";
import { ReactComponent as AddIcon } from "@assets/icons/add_icon.svg";
import { ReactComponent as ReturnIcon } from "@assets/icons/return.svg";
import emptyPlaceholderImg from "@assets/light_icons/empty_placeholder.svg";
import {
  ProvideStructureDataModel,
  CoordItemModel,
  BinderSequenceModel,
  CheckCoordPayloadModel,
  CoordResultModel,
} from "@services/home/model/home.model";
import {
  batchGetFileInfoApi,
  downloadFileApi,
} from "@services/common/common.api";
import { SourceEnum } from "@services/task/enum/task.enum";
import {
  vhhDesignTypeValidate,
  structSegmentValidate,
  seqSegmentValidate,
  binderSequenceValidate,
  antibodyChainValidate,
  isStructureSegmentValid,
} from "@utils/validator";
import { replaceWhitespace, sleep } from "@utils/common.util";
import ProgressBar from "../progress-bar";
import StructureViewer from "../structure-viewer";
import { getSelectedChainSequences } from "../../../utils/structure-process.util";
import styles from "./index.module.scss";

const { Text } = Typography;

interface Prop {
  row: BinderSequenceModel;
  cancel: () => void;
  submit: (data: ProvideStructureDataModel, fid: string) => void;
  reset: (seq: string) => void;
}

const ProvideStructureDialog: React.FC<Prop> = ({
  row,
  cancel,
  submit,
  reset,
}) => {
  const formRef = useRef<FormApi<ProvideStructureDataModel>>();

  const viewerRef = useRef<{
    loadStructureFromFile(file: File, isEditMode?: boolean): Promise<string>;
    clearStructure(): void;
    getPlugin(): PluginUIContext | undefined;
  }>();

  const selectedFileRef = useRef<File | null>(null);

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const uploadRef = useRef<Upload>(null);

  const currentFileId = useRef<string>();

  const initialData = [{} as CoordItemModel];

  const [loading, setLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const [checkResult, setCheckResult] = useState<CoordResultModel[]>([]);

  const [initialProvide, setInitialProvide] = useState<
    ProvideStructureDataModel | undefined
  >(
    row.coord_struct_id
      ? {
          input_type: row.pdb_input_type! as VhhDesignTypeEnum,
          input_value: row.pdb_input_value!,
          coord_items: row.coord_items!,
          entity_sequence: row.entity_sequence,
        }
      : undefined
  );

  const [isEmpty, setIsEmpty] = useState(true);

  const [isEdited, setIsEdited] = useState(true);

  const [filename, setFilename] = useState("");

  async function getFile() {
    if (!formRef.current) {
      return null;
    }

    try {
      await formRef.current.validate(["input_value"]);

      const inputType = formRef.current.getValue("input_type");
      const inputValue = formRef.current.getValue("input_value");

      let p: Promise<File | null> | null = null;

      if (inputType === VhhDesignTypeEnum.PdbID) {
        p = downloadPdbFileApi(inputValue);
      } else if (inputType === VhhDesignTypeEnum.UniProtID) {
        p = downloadAlphafoldApi(inputValue);
      } else if (inputType === VhhDesignTypeEnum.AntigenSequence) {
        p = getFoldSequenceStructureApi(inputValue);
      }

      const file = await p;

      return file;
    } catch (e) {
      if (e instanceof Error) {
        Toast.error(e.message);
      }

      return null;
    }
  }

  function openFile(file: File) {
    viewerRef.current?.loadStructureFromFile(file, true);
    setIsEmpty(false);
  }

  async function onOpenFile() {
    if (selectedFileRef.current) {
      openFile(selectedFileRef.current);
      return;
    }
    setLoading(true);
    const file = await getFile();
    setLoading(false);
    if (file) {
      selectedFileRef.current = file;
      openFile(file);
      setFilename(file.name);
    }
  }

  function handleCancel() {
    cancel();
  }

  function onValueChange(values: ProvideStructureDataModel) {
    setCheckResult([]);
  }

  const debouncedOnValueChange = useMemo(
    () => debounce(onValueChange, 500),
    [onValueChange]
  );

  async function uploadFile(file: File) {
    const formData = await getGzipFormData(file, SourceEnum.Upload);

    const { data } = await uploadFileApi(formData);

    return data.fid;
  }

  async function submitProvide() {
    if (!formRef.current) {
      return;
    }

    try {
      const values = await formRef.current.validate();

      setSubmitting(true);

      if (!selectedFileRef.current) {
        const f = await getFile();
        selectedFileRef.current = f;
        setFilename(f?.name || "");
      }

      const file = selectedFileRef.current!;

      let fid: string | undefined = undefined;
      if (currentFileId.current) {
        fid = currentFileId.current;
      } else {
        fid = await uploadFile(file);
        currentFileId.current = fid;
      }

      const checkParams: CheckCoordPayloadModel = {
        entity_type: row.entity_type,
        entity_sequence: values.entity_sequence || "",
        coord_struct_id: fid,
        coord_items: values.coord_items,
      };

      const { data } = await checkCoordValidityApi(checkParams);

      const hasError = data.coord_items.some((item) => {
        return item.is_ok === false;
      });

      if (hasError) {
        setSubmitting(false);

        setCheckResult(data.coord_items);
        return;
      } else {
        setCheckResult([]);
      }

      setSubmitting(false);

      const p: ProvideStructureDataModel = {
        ...values,
        input_value:
          values.input_type === VhhDesignTypeEnum.UploadStructure
            ? file.name
            : values.input_value,
      };
      submit(p, fid);
    } catch (e) {
      setSubmitting(false);
      console.error(e);
    }
  }

  function onFileChange(fileList: File[]) {
    const [file] = fileList;
    selectedFileRef.current = file;
    currentFileId.current = undefined;
    setFilename(file.name);
    openFile(file);
  }

  function handleSubmit() {
    if (!formRef.current) {
      return;
    }

    const coordItems = formRef.current.getValue("coord_items");

    const noCoord = (coordItems || []).every((item) => {
      const a = replaceWhitespace(item.struct_segment);
      const b = replaceWhitespace(item.seq_segment);

      return a === "" && b === "";
    });

    if (noCoord) {
      formRef.current.validate(["entity_sequence"]).then((v) => {
        reset(v.entity_sequence || "");
      });
      return;
    }

    submitProvide();
  }

  function handleReset() {
    formRef.current?.setValue("input_type", VhhDesignTypeEnum.PdbID);
    formRef.current?.setValue("input_value", "");
    formRef.current?.setValue("coord_items", initialData);
    viewerRef.current?.clearStructure();
    setIsEmpty(true);
  }

  function getPlaceholder(type?: string) {
    if (type === VhhDesignTypeEnum.PdbID) {
      return `e.g. 6xc3`;
    } else if (type === VhhDesignTypeEnum.UniProtID) {
      return `e.g. P0DTC2`;
    } else if (type === VhhDesignTypeEnum.AntigenSequence) {
      return `e.g. MFVFLVLLPLVSSQCV...`;
    }
    return "";
  }

  async function openFileFromId(id: string) {
    try {
      setLoading(true);
      const files = await batchGetFileInfoApi([id], "base");
      const [fileData] = files;

      const fileBlob = await downloadFileApi([id], "blob");

      const file = new File([fileBlob], `${fileData.name}`);

      selectedFileRef.current = file;
      setFilename(file.name);
      await sleep(100);

      await viewerRef.current?.loadStructureFromFile(file, true);
      setLoading(false);
      setIsEmpty(false);
    } catch (e) {
      setLoading(false);
    }
  }

  function onInputValueChange() {
    selectedFileRef.current = null;
    setFilename("");
  }

  function onViewStructure() {
    onOpenFile();
  }

  function onAcceptInvalid() {
    Toast.error(`Invalid file suffix: expected ".cif" or ".pdb"`);
  }

  function onUpload() {
    setTimeout(() => {
      uploadRef.current?.onClick();
    }, 0);
  }

  function onInputTypeChange(v: string | number | any[] | Record<string, any>) {
    if (v === VhhDesignTypeEnum.UploadStructure) {
      onUpload();
    }

    currentFileId.current = undefined;
    formRef.current?.reset(["input_value"]);
    formRef.current?.setValue("coord_items", [{} as CoordItemModel]);
    viewerRef.current?.clearStructure();
    selectedFileRef.current = null;
    setIsEmpty(true);
    setFilename("");
  }

  function onSeqBlur() {
    if (!formRef.current) {
      return;
    }
    const entitySequence = formRef.current.getValue("entity_sequence");

    const hasError = antibodyChainValidate(entitySequence || "", true);
    if (!hasError) {
      setIsEdited(false);
    }
  }

  function onEditSeq() {
    if (!isEdited) {
      setIsEdited(true);
    }

    setTimeout(() => {
      const textDom = inputRef.current;

      if (textDom) {
        const val = textDom.value;
        textDom.focus();
        textDom.setSelectionRange(val.length, val.length);
      }
    }, 0);
  }

  function onPasteSegments(e: React.ClipboardEvent<HTMLInputElement>) {
    const target = e.target as HTMLInputElement;
    const i = Number(target.dataset.index || "0");
    const coordItems = formRef.current?.getValue("coord_items") || [];
    const content = e.clipboardData.getData("Text");
    const trimedContent = content.trim();
    const msg = isStructureSegmentValid(trimedContent);
    if (msg) {
      Toast.info(msg);
      return;
    }

    const segments = replaceWhitespace(content);
    const segmentList = segments.split(",");

    if (coordItems) {
      const preList = coordItems.slice(0, i + 1);
      const afterList = coordItems.slice(i + 1);
      const middleList = Array(segmentList.length - 1).fill(
        undefined
      ) as CoordItemModel[];

      const newList = [...preList, ...middleList, ...afterList];

      for (let k = i; k < i + segmentList.length; k++) {
        if (!newList[k]) {
          newList[k] = {} as CoordItemModel;
        }
        newList[k].struct_segment = segmentList[k - i];
      }

      const tempList = newList.map((item) => {
        return undefined as unknown as CoordItemModel;
      });

      formRef.current?.setValue("coord_items", tempList);

      setTimeout(() => {
        formRef.current?.setValue("coord_items", newList);
      }, 100);
    }
  }

  function onAutoFill() {
    const plugin = viewerRef.current?.getPlugin();
    if (!plugin) {
      return;
    }

    const selection = plugin.managers.structure.selection;

    const entries = selection.entries;

    const values = Array.from(entries.values());

    if (values.length === 0) {
      Toast.info("Open a structure first.");
      return;
    }

    const [selectEntry] = values;

    const elements = selectEntry.selection.elements;

    if (elements.length > 1) {
      Toast.info("Selected residues must come from a single chain.");

      return;
    }

    if (elements.length === 0) {
      Toast.info(
        "You must select at least one residue from the structure to use autofill."
      );
      return;
    }

    const sites = getSitesLabelRange(selection);

    const sequences = getSelectedChainSequences(selection);

    if (sequences.length === 1) {
      formRef.current?.setValue("entity_sequence", sequences[0]);
    }

    const sitesList = sites.split(",");
    const coordItems = sitesList.map((item) => {
      const segment = item.split(":");
      const o: CoordItemModel = {
        struct_segment: item,
        seq_segment: segment[1],
      };

      return o;
    });

    formRef.current?.setValue("coord_items", []);

    setTimeout(() => {
      formRef.current?.setValue("coord_items", coordItems);
    }, 100);
  }

  useEffect(() => {
    if (row.coord_struct_id) {
      openFileFromId(row.coord_struct_id);
      currentFileId.current = row.coord_struct_id;
    }
  }, []);

  useEffect(() => {
    const seq = replaceWhitespace(row.entity_sequence);
    if (seq) {
      setIsEdited(false);
    }
  }, [row.entity_sequence]);

  return (
    <Modal
      title="Provide structure"
      maskClosable={false}
      visible={true}
      onCancel={handleCancel}
      closeOnEsc={true}
      modalContentClass={styles.modalContent}
      //   width="1240px"
      style={{
        width: "100%",
        height: "100%",
        padding: "60px 100px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      centered
      footer={
        <div>
          <Button theme="light" type="tertiary" onClick={handleReset}>
            Reset
          </Button>

          <Button
            theme="solid"
            type="primary"
            onClick={handleSubmit}
            loading={submitting}
          >
            Ok
          </Button>
        </div>
      }
    >
      <div className={styles.container}>
        <div className={styles.viewerContainer}>
          <StructureViewer
            from="dialog"
            ref={viewerRef}
            sequenceTitle="Reference Structure"
            showCopy={true}
            showViewportControl={false}
            selectionToolsHideConfig={{
              granularity: false,
              union: false,
              subtract: false,
              intersect: false,
              set: false,
              theme: true,
              componentAdd: true,
              componentRemove: true,
              undo: true,
              help: true,
              cancel: true,
            }}
            style={{ position: "absolute" }}
            showAnimation={false}
            // fileLoaded={onFileLoaded}
            // pluginChange={onPluginChange}
          />

          {loading && <ProgressBar close={() => {}} />}

          {!loading && isEmpty && (
            <div className={styles.emptyContainer}>
              <img src={emptyPlaceholderImg} />
              <div className={styles.text}>
                Reference structure will be displayed here.
              </div>
            </div>
          )}
        </div>
        <div className={styles.formContainer}>
          <Form
            initValues={
              initialProvide
                ? initialProvide
                : {
                    input_type: VhhDesignTypeEnum.PdbID,
                    coord_items: initialData,
                    entity_sequence: row.entity_sequence,
                    input_value: "",
                  }
            }
            getFormApi={(formApi) => (formRef.current = formApi)}
            autoComplete="off"
            onValueChange={debouncedOnValueChange}
          >
            {({
              formState,
            }: {
              formState: FormState<ProvideStructureDataModel>;
            }) => (
              <>
                <Form.InputGroup
                  style={{ flexWrap: "nowrap" }}
                  label={{ text: "Reference structure", required: false }}
                >
                  <Form.Select
                    style={{ width: 100 }}
                    field="input_type"
                    rules={[{ required: true }]}
                    onChange={onInputTypeChange}
                  >
                    {VHH_DESIGN_TYPES.map((item) => {
                      return (
                        <Form.Select.Option value={item.value} key={item.value}>
                          {item.name}
                        </Form.Select.Option>
                      );
                    })}
                  </Form.Select>
                  {formState.values?.input_type ===
                  VhhDesignTypeEnum.UploadStructure ? (
                    <Form.Upload
                      field="upload"
                      ref={uploadRef}
                      action=""
                      showUploadList={false}
                      customRequest={() => {}}
                      onAcceptInvalid={onAcceptInvalid}
                      draggable={true}
                      accept=".cif,.pdb"
                      className={styles.uploadContainer}
                      limit={1}
                      dragMainText={`${
                        filename || "Click or drag a file here to upload"
                      }`}
                      onFileChange={onFileChange}
                    />
                  ) : (
                    <Form.Input
                      style={{ width: 234 }}
                      field="input_value"
                      placeholder={getPlaceholder(formState.values?.input_type)}
                      trigger="blur"
                      validate={(v: string) =>
                        vhhDesignTypeValidate(v, formState.values?.input_type)
                      }
                      onChange={onInputValueChange}
                      onEnterPress={onViewStructure}
                      suffix={
                        <span
                          onClick={onViewStructure}
                          className={styles.returnWrapper}
                        >
                          <ReturnIcon />
                        </span>
                      }
                    />
                  )}
                </Form.InputGroup>

                <Tooltip content='Copy full sequence of the chain that the selection belongs to "Input Sequence", then autofill the sequence-structure relation table.'>
                  <Button
                    className="hightlight-btn"
                    style={{ width: "100%", margin: "6px 0" }}
                    onClick={onAutoFill}
                  >
                    Autofill from structure selection
                  </Button>
                </Tooltip>

                <Form.TextArea
                  className={classNames({
                    [styles.hidden]: !isEdited,
                  })}
                  rows={7}
                  placeholder={getBinderPlaceholder(row.entity_type)}
                  validate={(v: string) =>
                    binderSequenceValidate(v, row.entity_type)
                  }
                  ref={inputRef}
                  trigger="blur"
                  field="entity_sequence"
                  label={{ text: "Input Sequence", required: false }}
                  style={{ width: "100%" }}
                  onBlur={onSeqBlur}
                />

                <div
                  className={classNames({
                    "sequence-container": true,
                    [styles.hidden]: isEdited,
                  })}
                  style={{ maxHeight: "250px", overflow: "auto" }}
                  onPointerDown={onEditSeq}
                >
                  <div className="seq-wrapper">
                    {replaceWhitespace(formState.values?.entity_sequence || "")
                      .split("")
                      .map((residue, index) => {
                        return (
                          <span className="seq" key={index}>
                            {residue}
                            <span className="label">{index + 1}</span>
                          </span>
                        );
                      })}
                  </div>
                </div>

                <ArrayField field="coord_items">
                  {({ add, arrayFields }) => (
                    <React.Fragment>
                      <div
                        className={styles.row}
                        style={{ marginBottom: "4px", marginTop: "6px" }}
                      >
                        <div className={styles.col}>Structure segment</div>
                        <div className={styles.col}>Sequence segment</div>
                      </div>
                      <div className={styles.content}>
                        {arrayFields.map(({ field, key, remove }, i) => (
                          <React.Fragment key={key}>
                            <div className={styles.row}>
                              <Form.Input
                                // pure
                                field={`${field}[struct_segment]`}
                                noLabel
                                size="small"
                                trigger="blur"
                                style={{ width: "100%" }}
                                placeholder="e.g., H:3-20"
                                validate={structSegmentValidate}
                                fieldClassName={styles.inputWrapper}
                                data-index={i}
                                onPaste={onPasteSegments}
                              ></Form.Input>
                              <Form.Input
                                // pure
                                trigger="blur"
                                size="small"
                                field={`${field}[seq_segment]`}
                                noLabel
                                style={{ width: "100%" }}
                                placeholder="e.g., 1-17"
                                validate={(v: string) =>
                                  seqSegmentValidate(
                                    v,
                                    formState.values?.entity_sequence
                                  )
                                }
                                fieldClassName={styles.inputWrapper}
                              ></Form.Input>

                              <Button
                                className={styles.lightText}
                                theme="borderless"
                                type="tertiary"
                                size="small"
                                icon={<MinusIcon />}
                                onClick={remove}
                              />
                            </div>
                            {checkResult.length > 0 &&
                              !checkResult[i].is_ok && (
                                <div className={styles.error}>
                                  {checkResult[i].fail_message}
                                </div>
                              )}
                          </React.Fragment>
                        ))}
                      </div>

                      <div className={styles.footer}>
                        <Button
                          onClick={add}
                          type="tertiary"
                          icon={<AddIcon />}
                          theme="borderless"
                          className={styles.btn}
                        ></Button>

                        {arrayFields.length === 0 && (
                          <span className={styles.addText}>
                            Click to add a segment
                          </span>
                        )}
                      </div>
                    </React.Fragment>
                  )}
                </ArrayField>
              </>
            )}
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default ProvideStructureDialog;
